import { AxiosRequestConfig } from 'axios';

import { SPEND_QUERY_DATASET_ID } from 'constants/dataset';
import { EmailMessageType } from 'constants/email';
import { RECIPIENT_KEY_TYPE, RECIPIENT_TYPE } from 'constants/recipient';
import { ItemSchemaNameType } from 'constants/schema';
import { SEARCH_RESULT_VIEW_MODE, SearchResultViewModeType } from 'constants/search';
import {
  GlobalSupplierListAssignmentType,
  GlobalSupplierListType,
  prepareGlobalSupplierListForRequest
} from 'util/globalSupplierList';
import { isRegisteredSupplierFilterEnabled } from 'util/filter';
import { buildSpendQuery, getFrequentContactScoresFilter } from 'util/spendQuery';
import { SupplierGroupType } from 'util/transform/supplierGroup';

//
// --- Types --

type SortDirection = 'ASC' | 'DESC';
export type DiversityClassificationFilter = { id: string; isWoodMacFilter?: boolean };

export interface DiscoveryRequestFilterOptions {
  countryFilters?: ReadonlyArray<string>;
  diversityClassificationFilters?: ReadonlyArray<string>;
  diversityFilters?: ReadonlyArray<string>;
  invitedEventCountFilters?: ReadonlyArray<number>;
  invitedPortalCountFilters?: ReadonlyArray<number>;
  itemFilters?: ReadonlyArray<number>;
  qualificationFilters?: ReadonlyArray<string>;
  segmentationFilters?: ReadonlyArray<string>;
  sourceFilters?: ReadonlyArray<string>;
  stateFilters?: ReadonlyArray<string>;
  supplierIntelSchemaName?: ItemSchemaNameType | '';
}

interface SortListItem {
  direction: SortDirection;
  property: string;
}

// Request Option Types

interface CreateSupplierGroupCompanyPerson {
  firstName: string;
  lastName: string;
  email: string;
  workPhone?: string;
}

interface CreateSupplierGroupCompanyOptions {
  groupId?: number;
  person?: CreateSupplierGroupCompanyPerson;
}

interface CreateSupplierGroupCompanyPersonOptions {
  groupId?: number;
}

interface CreateSupplierGroupOptions {
  personKey?: number;
}

interface GetBidTeamsOptions {
  useCacheBustTimestamp?: boolean;
}

interface GetFrequentContactScoresOptions {
  pageSize?: number;
  pointOfContactScore?: number;
  sortList?: ReadonlyArray<SortListItem>;
  spendQuery?: any;
  startIndex?: number;
  useDefaultColumns?: boolean;
}

interface GetMatchesDiscoveryFiltersMapForEipCompanyKeysIdsOptions extends DiscoveryRequestFilterOptions {
  eipCompanyKeys?: ReadonlyArray<number>;
}

interface GetMatchesDiscoveryFiltersMapForSupplierIdsOptions extends DiscoveryRequestFilterOptions {
  supplierIds?: ReadonlyArray<string>;
}

interface GetPointOfContactScoreForPeopleOptions {
  minimumPowerScore?: number;
  pageSize?: number;
}

interface GetSupplierGroupsOptions {
  useCacheBustTimestamp?: boolean;
}

interface RunAllCompanySearchOptions extends Omit<DiscoveryRequestFilterOptions, 'diversityClassificationFilters'> {
  page?: number;
  pageSize?: number;
  diversityClassificationFilters?: ReadonlyArray<DiversityClassificationFilter>;
  useNewSupplierSearch?: boolean;
}

interface RunSearchOptions extends Omit<DiscoveryRequestFilterOptions, 'diversityClassificationFilters'> {
  excludePeople?: boolean;
  isCompanySearch?: boolean;
  viewMode?: SearchResultViewModeType;
  diversityClassificationFilters?: ReadonlyArray<DiversityClassificationFilter>;
  useNewSupplierSearch?: boolean;
}

interface SendBidEventMailOptions {
  firstTime?: boolean;
  newAddress?: string;
}

//
// --- Base Requests ---

/**
 * Base configuration object that allow us to alter all API requests used in
 * the application. This should not be used externally, but should be used to build out
 * configuration functions/objects below.
 */
const baseAxiosConfig: Partial<AxiosRequestConfig> = {};

/**
 * Service level base configuration objects that allow us to alter API requests at a
 * "service" level. These objects should not be used externally, but should be used to build
 * out configuration functions/objects below.
 */

// auth service
const baseAuthAxiosConfig: Partial<AxiosRequestConfig> = {
  ...baseAxiosConfig,
  baseURL: '/auth'
};

// sourcingIntel service
const baseSourcingIntelAxiosConfig: Partial<AxiosRequestConfig> = {
  ...baseAxiosConfig,
  baseURL: '/sourcingIntel'
};

// supplierIntel service
const baseSupplierIntelAxiosConfig: Partial<AxiosRequestConfig> = {
  ...baseAxiosConfig,
  baseURL: '/supplierIntel'
};

// spendIntel Query Service
const baseSpendIntelQueryServiceAxiosConfig: Partial<AxiosRequestConfig> = {
  ...baseAxiosConfig,
  baseURL: '/siqs/spend/dataset'
};

// supplierSearch service and common EIP controllers (DO NOT CONFUSE WITH /supplierIntel/supplierSearch)
const baseSupplierSearchAxiosConfig: Partial<AxiosRequestConfig> = {
  ...baseAxiosConfig,
  baseURL: '/supplierSearch'
};

//
// --- Filter Endpoints ---

const getSearchFilters = (isIPAFB = false): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  params: { isIPAFB: isIPAFB },
  url: '/advancedSearch/loadSearchFilters'
});

const getSupplierIntelSchemaName = (): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  url: '/advancedSearch/schemaName'
});

//
// --- Permission Endpoints ---

const getLoggedInPersonCompany = (): AxiosRequestConfig => ({
  ...baseAuthAxiosConfig,
  url: '/loggedInCompany'
});

const hasScreenPermission = (productKey: number, screenDescrKey: number): AxiosRequestConfig => ({
  ...baseAuthAxiosConfig,
  params: {
    productKey,
    screenDescrKey
  },
  url: '/hasScreenPermission'
});

const hasScreenPermissionForEvent = (
  productKey: number,
  screenDescrKey: number,
  offeringKey: number
): AxiosRequestConfig => ({
  ...baseAuthAxiosConfig,
  params: {
    productKey,
    screenDescrKey,
    offeringKey
  },
  url: '/hasScreenPermissionForEvent'
});

const hasScreenPermissionForPortal = (
  productKey: number,
  screenDescrKey: number,
  portalId: number
): AxiosRequestConfig => ({
  ...baseAuthAxiosConfig,
  params: {
    productKey,
    screenDescrKey,
    portalId
  },
  url: '/hasScreenPermissionForPortal'
});

//
// --- Portal Endpoints ---

/**
 * Endpoint for adding an existing company to an existing supplier group. This should not be when
 * creating a new company. Use `createSupplierGroupCompany` instead
 */
const addCompanyToSupplierGroup = (
  portalId: number,
  supplierGroupId: number,
  companyKey: number
): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  method: 'post',
  url: `/supplierSearch/portal/${portalId}/supplierGroup/${supplierGroupId}/company/${companyKey}`
});

/**
 * Endpoint for adding an existing person to an existing supplier group. This should not be when
 * creating a new person. Use `createSupplierGroupPerson` instead
 */
const addPersonToSupplierGroup = (
  portalId: number,
  supplierGroupId: number,
  personKey: number
): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  method: 'post',
  url: `/supplierSearch/portal/${portalId}/supplierGroup/${supplierGroupId}/person/${personKey}`
});

const createSupplierGroup = (
  portalId: number,
  companyKey: number,
  opts: CreateSupplierGroupOptions = {}
): AxiosRequestConfig => {
  const { personKey } = opts;

  const data = { companyKey };

  return {
    ...baseSupplierIntelAxiosConfig,
    data: personKey ? { ...data, personKey } : data,
    method: 'post',
    url: `/supplierSearch/portalId/${portalId}/supplierGroups`
  };
};
const getIsViewOnlyForBidEvent = (offeringKey: number): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  url: `/supplierSearch/offeringKey/${offeringKey}/isViewOnly`
});

const getIsViewOnlyForPortal = (portalId: number): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  url: `/supplierSearch/portalId/${portalId}/isViewOnly`
});

/**
 * Endpoint for creating a new company (with an optional new person) and adding to a new or
 * existing supplier group. This should not be used for adding existing companies to a supplier
 * group. Use `addCompanyToSupplierGroup` instead
 */

const createSupplierGroupCompany = (
  portalId: number,
  companyName: string,
  opts: CreateSupplierGroupCompanyOptions = {}
): AxiosRequestConfig => {
  const { groupId, person } = opts;

  const hasPerson = !!person?.email && !!person?.firstName && !!person?.lastName;

  return {
    ...baseSupplierIntelAxiosConfig,
    data: {
      companyName,
      groupId: groupId ? `${groupId}` : undefined,
      personEmail: hasPerson ? person.email : undefined,
      personFirstName: hasPerson ? person.firstName : undefined,
      personLastName: hasPerson ? person.lastName : undefined,
      personWorkPhone: hasPerson && person.workPhone ? person.workPhone : undefined
    },
    method: 'post',
    url: `/supplierSearch/portalId/${portalId}/supplierCompany`
  };
};

/**
 * Endpoint for creating a new person and adding to a new or existing supplier group. This should not
 * be used for adding existing people to a supplier group. Use `addPersonToSupplierGroup` instead
 */
const createSupplierGroupPerson = (
  portalId: number,
  companyKey: number,
  person: CreateSupplierGroupCompanyPerson,
  opts: CreateSupplierGroupCompanyPersonOptions = {}
): AxiosRequestConfig => {
  const { groupId } = opts;

  return {
    ...baseSupplierIntelAxiosConfig,
    data: {
      groupId: groupId ? `${groupId}` : undefined,
      personEmail: person.email,
      personFirstName: person.firstName,
      personLastName: person.lastName,
      personWorkPhone: person?.workPhone ? person.workPhone : undefined
    },
    method: 'post',
    url: `/supplierSearch/portalId/${portalId}/company/${companyKey}`
  };
};

const getPortal = (portalId: number): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  url: `/supplierPortal/${portalId}`
});

const getSupplierGroups = (portalId: number, opts: GetSupplierGroupsOptions = {}): AxiosRequestConfig => {
  const { useCacheBustTimestamp } = opts;

  return {
    ...baseSupplierIntelAxiosConfig,
    params: useCacheBustTimestamp ? { timestamp: Date.now() } : undefined,
    url: `/supplierSearch/portalId/${portalId}/supplierGroups`
  };
};

const removeSupplierCompanyFromPortal = (portalId: number, companyKey: number): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  method: 'delete',
  url: `/supplierSearch/portalId/${portalId}/supplier/${companyKey}`
});

const removeSupplierCompanyPersonFromPortal = (portalId: number, personKey: number): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  method: 'delete',
  url: `/supplierSearch/portalId/${portalId}/supplierUser/${personKey}`
});

const removeSupplierGroup = (portalId: number, supplierGroupId: number): AxiosRequestConfig => {
  return {
    ...baseSupplierIntelAxiosConfig,
    method: 'delete',
    url: `/supplierSearch/portalId/${portalId}/supplierGroups/${supplierGroupId}`
  };
};

const updateSupplierGroup = (
  portalId: number,
  supplierGroupId: number,
  updatedSupplierGroup: SupplierGroupType
): AxiosRequestConfig => {
  // NOTE: this endpoint currently only supports editing a supplier group name, and expects the request body to be a string
  // with the new name

  return {
    ...baseSupplierIntelAxiosConfig,
    headers: { 'Content-Type': 'text/plain' },
    data: updatedSupplierGroup?.name ? updatedSupplierGroup.name : '',
    method: 'put',
    url: `/supplierSearch/portalId/${portalId}/supplierGroups/${supplierGroupId}`
  };
};

const updateSupplierGroupMainContact = (portalId: number, personId: number): AxiosRequestConfig => ({
  ...baseSupplierIntelAxiosConfig,
  method: 'put',
  url: `/supplierSearch/portalId/${portalId}/supplierUser/${personId}`
});

//
// --- Spend Intel Query Service ---

/**
 * run spend intel query service frequent contact score request
 */
const getFrequentContactScores = (
  buyerCompanyKey: string,
  options: GetFrequentContactScoresOptions = {}
): AxiosRequestConfig => {
  const {
    pageSize,
    pointOfContactScore = 0,
    sortList = [
      { direction: 'DESC', property: 'poc' },
      { direction: 'ASC', property: 'lastName' }
    ],
    spendQuery,
    startIndex = 0,
    useDefaultColumns = true
  } = options;

  const useParams = !!pageSize;

  return {
    ...baseSpendIntelQueryServiceAxiosConfig,
    data: buildSpendQuery({
      filters: getFrequentContactScoresFilter(buyerCompanyKey, pointOfContactScore),
      spendQuery,
      sortList,
      useDefaultColumns
    }),
    method: 'post',
    params: useParams ? { size: pageSize, startIdx: startIndex } : undefined,
    url: `/${SPEND_QUERY_DATASET_ID.CONTACT}/query`
  };
};

const getPointOfContactScoreForPeople = (
  buyerCompanyKey: number,
  personIds: ReadonlyArray<number>,
  options: GetPointOfContactScoreForPeopleOptions = {}
): AxiosRequestConfig => {
  const { minimumPowerScore = 1, pageSize = 0 } = options;

  // make sure we are working with an array
  const filterPersonIds = Array.isArray(personIds) && personIds.length > 0 ? personIds : [];

  return {
    ...baseSpendIntelQueryServiceAxiosConfig,
    data: buildSpendQuery({
      filters: [
        { or: [{ eq: { BUYERCOMPANYKEY: buyerCompanyKey } }, { isNull: 'BUYERCOMPANYKEY' }] },
        { gt: { POC: minimumPowerScore - 1 } },
        filterPersonIds.length === 1
          ? { eq: { PERSONKEY: filterPersonIds[0] } }
          : { in: { PERSONKEY: filterPersonIds } }
      ],
      useDefaultColumns: true
    }),
    method: 'post',
    params: pageSize > 0 ? { size: pageSize, startIdx: 0 } : undefined,
    url: `/${SPEND_QUERY_DATASET_ID.CONTACT}/query`
  };
};

//
// --- Sourcing Endpoints ---

const checkForDuplicateCompanyName = (companyName: string): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  params: { companyName },
  url: `/companies/checkDuplicateCompanyName`
});

const checkForDuplicateSupplierEmail = (email: string): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  data: email,
  headers: { 'Content-Type': 'text/plain' },
  method: 'post',
  url: '/commonModule/person/checkSupplierDuplicateEmail'
});

const createBidTeam = (data: FormData): AxiosRequestConfig => ({
  ...baseAxiosConfig,
  data,
  headers: { 'Content-Type': 'multipart/form-data' },
  method: 'post',
  url: '/createBidTeam.do'
});

const createBidTeamMember = (data: FormData): AxiosRequestConfig => ({
  ...baseAxiosConfig,
  data,
  headers: { 'Content-Type': 'multipart/form-data' },
  method: 'post',
  url: '/createBidTeamMember.do'
});

const getBidEvent = (offeringKey: number): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  url: `/events/${offeringKey}`
});

/** used as part of supplier lists **/
const getBidEventItems = (offeringKey: number): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  url: `/supplierSearch/offeringKey/${offeringKey}`
});

const getBidEventItemSupplierList = (itemKey: string): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  url: `/supplierSearch/itemKey/${itemKey}`
});

const getBidEventMailLog = (offeringKey: number, personKey: number): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  params: {
    offeringKey,
    personKey
  },
  url: '/bidEventMail/emailLog'
});

const getBidTeams = (offeringKey: number, opts: GetBidTeamsOptions = {}): AxiosRequestConfig => {
  // caching the bidTeams causes UI to not refresh when it should in IE11.
  const { useCacheBustTimestamp = true } = opts;

  return {
    ...baseSourcingIntelAxiosConfig,
    params: useCacheBustTimestamp ? { timestamp: Date.now() } : undefined,
    url: `/bidEventSetupSuppliers/${offeringKey}/bidTeams`
  };
};

const getSupplierRequests = (offeringKey: number): AxiosRequestConfig => ({
  ...baseSourcingIntelAxiosConfig,
  url: `/bidEventSetupSuppliers/${offeringKey}/suppliersRequestingAccess`
});

const getUniqueBuyerCompanies = (): AxiosRequestConfig => {
  return {
    ...baseSourcingIntelAxiosConfig,
    url: '/companies/uniqueBuyerCompanies'
  };
};

const grantSupplierAccess = (
  offeringKey: number,
  personKey: number,
  leadCompanyKey: number,
  shouldHaveAccess: boolean,
  comments: string
): AxiosRequestConfig => {
  const formData = new FormData();
  formData.append('offeringkey', `${offeringKey}`);
  formData.append('personkey', `${personKey}`);
  formData.append('grantAccess', `${shouldHaveAccess}`);
  formData.append('grantorcomments', comments);
  formData.append('leadCompanyKey', `${leadCompanyKey}`);

  return {
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    method: 'post',
    url: '/grantAccessSupplier.do'
  };
};

const inviteSupplier = (data: FormData): AxiosRequestConfig => ({
  data,
  headers: { 'Content-Type': 'multipart/form-data' },
  method: 'post',
  url: '/inviteNewSupplier.do'
});

const createNewCompany = (data: FormData): AxiosRequestConfig => ({
  data,
  headers: { 'Content-Type': 'multipart/form-data' },
  method: 'post',
  url: '/createNewCompany.do'
});

const prepareSupplierCompanyPersonNotifyEmail = (offeringKey: number, personKey: number): AxiosRequestConfig => ({
  params: {
    okey: offeringKey,
    personKey
  },
  url: '/configSupplierNotifyEmail.do'
});

const prepareSupplierCompanyPersonReInviteEmail = (
  offeringKey: number,
  personKey: number,
  email: string
): AxiosRequestConfig => ({
  params: {
    newEmailAddress: email,
    okey: offeringKey,
    personKey,
    reinviteAction: 'on'
  },
  url: '/configSupplierReinviteEmail.do'
});

const prepareSupplierCompanyPersonReNotifyEmail = (
  offeringKey: number,
  personKey: number,
  email: string
): AxiosRequestConfig => ({
  params: {
    newEmailAddress: email,
    okey: offeringKey,
    personKey,
    reinviteAction: 'on'
  },
  url: '/configSupplierRenotifyEmail.do'
});

const removeBidTeam = (offeringKey: number, leadCompanyKey: number, bidSequenceNumber: number): AxiosRequestConfig => ({
  params: {
    altBidSeqNo: bidSequenceNumber,
    leadCompanyKey,
    okey: offeringKey
  },
  url: '/removeBidTeam.do'
});

const removeSupplierCompanyFromBidTeam = (
  offeringKey: number,
  supplierCompanyKey: number,
  bidTeamLeadCompanyKey: number,
  bidSequenceNumber: number
): AxiosRequestConfig => ({
  params: {
    altBidSeqNo: bidSequenceNumber,
    leadCompanyKey: bidTeamLeadCompanyKey,
    memberCompanyKey: supplierCompanyKey,
    okey: offeringKey
  },
  url: '/removeBidTeamMember.do'
});

const removeSupplierCompanyPersonFromBidTeam = (offeringKey: number, personKey: number): AxiosRequestConfig => ({
  params: {
    okey: offeringKey,
    personKey
  },
  url: '/removeBidTeamPerson.do'
});

const sendBidEventMail = (
  offeringKey: number,
  messageType: EmailMessageType,
  personKey: number,
  opts: SendBidEventMailOptions = {}
): AxiosRequestConfig => {
  const { firstTime = false, newAddress } = opts;

  return {
    ...baseSourcingIntelAxiosConfig,
    data: {
      firstTime,
      messageType,
      newAddress,
      recipientKeyType: RECIPIENT_KEY_TYPE.PERSON,
      recipients: [personKey],
      recipientType: RECIPIENT_TYPE.SUPPLIER
    },
    method: 'post',
    url: `/bidEventMail/send/${offeringKey}`
  };
};

const sendSupplierBulkEmail = (offeringKey: number, personKeys: ReadonlyArray<number>): AxiosRequestConfig => {
  // normally use `params` for query strings, but default paramSerializer adds brackets to repeated query string keys
  // using `params` and `paramsSerialized` options would be ideal, but it would require a (non-MIT) library, or some other library
  // just build `url` manually instead for this case

  const personParamString =
    Array.isArray(personKeys) && personKeys.length ? `&person=${personKeys.join('&person=')}` : '';

  return {
    method: 'post',
    url: `/sendSupplierBulkEmail.do?okey=${offeringKey}${personParamString}`
  };
};

const updateBidTeam = (bidTeam: any): AxiosRequestConfig => {
  const bidTeamFormData = new FormData();
  bidTeamFormData.append('okey', bidTeam?.compositeKey?.offeringKey);
  bidTeamFormData.append('leadcompanykey', bidTeam?.compositeKey?.leadCompanyKey);
  bidTeamFormData.append('leadcompany', bidTeam?.compositeKey?.leadCompanyKey);
  bidTeamFormData.append('altbidseqno', bidTeam?.compositeKey?.altBidSeqNo);
  bidTeamFormData.append('teamname', bidTeam?.name);
  bidTeamFormData.append('bidstatus', bidTeam?.bidStatus);
  bidTeamFormData.append('bidintention', bidTeam?.bidIntention);
  bidTeamFormData.append('teamcomment', ''); // No longer used but needed by legacy code

  // Causes an error if maincontact is sent but not defined
  if (bidTeam?.mainContactPersonKey) {
    bidTeamFormData.append('maincontact', bidTeam?.mainContactPersonKey);
  }

  // Array data for x-www-form-urlencoded must be sent as multiple params with the same value
  if (bidTeam?.bidTeamMemberKeys) {
    for (const memberKey of bidTeam.bidTeamMemberKeys) {
      bidTeamFormData.append('members', memberKey);
      bidTeamFormData.append(`teamrole-${memberKey}`, 'Not Specified'); // No longer used but needed by legacy code
    }
  }

  return {
    data: bidTeamFormData,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    method: 'post',
    url: '/updateBidTeam.do'
  };
};

const updateBidTeamApprovalStatus = (
  offeringKey: number,
  approvalStatus: string,
  leadCompanyKey: number,
  bidSequenceNumber: number
): AxiosRequestConfig => ({
  params: {
    altBidSeqNo: bidSequenceNumber,
    bidstatus: approvalStatus,
    leadCompanyKey,
    okey: offeringKey
  },
  url: '/updateBidTeamApprovalStatus.do'
});

const updateBidTeamBidIntention = (
  offeringKey: number,
  bidIntention: string,
  leadCompanyKey: number,
  bidSequenceNumber: number
): AxiosRequestConfig => ({
  params: {
    altBidSeqNo: bidSequenceNumber,
    bidintention: bidIntention,
    leadCompanyKey,
    okey: offeringKey
  },
  url: '/updateBidTeamIntention.do'
});

//
// --- Market List Endpoints ---

/**
 * create a new Global Supplier list
 */
const addGlobalSupplierList = (list: GlobalSupplierListType, productKey: number): AxiosRequestConfig => {
  return {
    ...baseSupplierSearchAxiosConfig,
    data: prepareGlobalSupplierListForRequest(list, { isNewList: true }),
    method: 'post',
    url: `/lists/createList/product/${productKey}`
  };
};

/**
 * Add list assignments to a list. This endpoint only works to add suppliers. If you need
 * to remove suppliers, use updateGlobalSupplierList instead. This endpoint is a little more secure/reliable
 * for adding suppliers because other changes to list data are better protected by the API.
 */
const addGlobalSupplierListAssignments = (
  globalSupplierList: GlobalSupplierListType,
  companyKeysToAdd: ReadonlyArray<number>,
  productKey: number
): AxiosRequestConfig => {
  // we can exclude all existing assignments, only only need to set numericKey1 for new assignments
  const globalSupplierListWithNewAssignmentsOnly: GlobalSupplierListType = {
    ...globalSupplierList,
    miListAssignments: Array.isArray(companyKeysToAdd)
      ? companyKeysToAdd.map(companyKeyToAdd => ({ numericKey1: companyKeyToAdd } as GlobalSupplierListAssignmentType))
      : []
  };

  return {
    ...baseSupplierSearchAxiosConfig,
    data: prepareGlobalSupplierListForRequest(globalSupplierListWithNewAssignmentsOnly),
    method: 'post',
    url: `/lists/saveListAssignments/product/${productKey}`
  };
};

/**
 * Load market list edit permission response object for a given product
 */
const getSupplierListPermissionsForProduct = (productKey: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/lists/permissions/${productKey}`
});

/**
 * get list of suppliers for a specific list
 */
const getSupplierListResults = (listKey: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/lists/supplierLists/${listKey}`
});

/**
 * get lists of a certain type (1 = suppliers) for a given category (companyLists, myLists, etc)
 */
const getSupplierListsByCategory = (listCategory: string, listType: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/lists/${listCategory}/listType/${listType}`
});

const removeGlobalSupplierList = (listKey: number, productKey: number): AxiosRequestConfig => {
  return {
    ...baseSupplierSearchAxiosConfig,
    method: 'delete',
    url: `/lists/deleteList/product/${productKey}/list/${listKey}`
  };
};

/**
 * Updates an existing Global Supplier list, including list assignments
 */
const updateGlobalSupplierList = (list: GlobalSupplierListType, productKey: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  data: prepareGlobalSupplierListForRequest(list),
  method: 'put',
  url: `/lists/updateList/product/${productKey}`
});

//
// --- Supplier Endpoints ---

const getSupplierVendorMappings = (memberCompanyKeys: ReadonlyArray<number>): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  data: memberCompanyKeys,
  method: 'post',
  url: '/search/supplierVendorMappings'
});

//
// --- Supplier Search Endpoints ---

const getActiveInvitedSupplierPersonsByIds = (personKeys: ReadonlyArray<number>): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  data: personKeys,
  method: 'post',
  url: '/lists/getActiveInvitedSupplierPersonsByIds'
});

const getCountryList = (): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/search/countries`
});

const getDiversityTypeMap = (): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/search/diversity`
});

const getMatchesDiscoveryFiltersMapForEipCompanyKeysIds = (
  opts: GetMatchesDiscoveryFiltersMapForEipCompanyKeysIdsOptions = {}
): AxiosRequestConfig => {
  const {
    countryFilters = [],
    diversityClassificationFilters = [],
    diversityFilters = [],
    eipCompanyKeys = [],
    invitedEventCountFilters = [],
    invitedPortalCountFilters = [],
    itemFilters = [],
    qualificationFilters = [],
    segmentationFilters = [],
    sourceFilters = [],
    stateFilters = [],
    supplierIntelSchemaName
  } = opts;

  return {
    ...baseSupplierSearchAxiosConfig,
    data: {
      countryFilters,
      diversityClassificationFilters,
      diversityFilters,
      eipCompanyKeys,
      enableRegisteredSupplierFilter: isRegisteredSupplierFilterEnabled(sourceFilters),
      eventsInvitedFilters: invitedEventCountFilters,
      portalsInvitedFilters: invitedPortalCountFilters,
      itemFilters,
      qualificationFilters,
      segmentationFilters,
      stateFilters,
      supplierItemsSchema: supplierIntelSchemaName
    },
    method: 'post',
    url: '/search/discovery/eipCompanyKeys'
  };
};

const getMatchesDiscoveryFiltersMapForSupplierIds = (
  opts: GetMatchesDiscoveryFiltersMapForSupplierIdsOptions = {}
): AxiosRequestConfig => {
  const {
    countryFilters = [],
    diversityClassificationFilters = [],
    diversityFilters = [],
    invitedEventCountFilters = [],
    invitedPortalCountFilters = [],
    itemFilters = [],
    qualificationFilters = [],
    segmentationFilters = [],
    sourceFilters = [],
    stateFilters = [],
    supplierIds = [],
    supplierIntelSchemaName
  } = opts;

  return {
    ...baseSupplierSearchAxiosConfig,
    data: {
      countryFilters,
      diversityClassificationFilters,
      diversityFilters,
      enableRegisteredSupplierFilter: isRegisteredSupplierFilterEnabled(sourceFilters),
      eventsInvitedFilters: invitedEventCountFilters,
      portalsInvitedFilters: invitedPortalCountFilters,
      itemFilters,
      qualificationFilters,
      segmentationFilters,
      stateFilters,
      supplierIds,
      supplierItemsSchema: supplierIntelSchemaName
    },
    method: 'post',
    url: '/search/discovery/supplierIds'
  };
};

const getRelatedCompaniesByAsapSupplierId = (companyKey: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/search/relatedCompanies/asapSupplierId/${companyKey}`
});

const getRelatedCompaniesByEipCompanyKey = (companyKey: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/search/relatedCompanies/eipCompanyKey/${companyKey}`
});

const getSupplierUsersByAsapSupplierId = (asapSupplierId: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/search/supplierUsers/asapSupplierId/${asapSupplierId}`
});

const getSupplierUsersByEipCompanyKey = (eipCompanyKey: number): AxiosRequestConfig => ({
  ...baseSupplierSearchAxiosConfig,
  url: `/search/supplierUsers/eipCompanyKey/${eipCompanyKey}`
});

const getNewSupplierSearchFeatureToggleFlag = (): AxiosRequestConfig => ({
  ...baseAxiosConfig,
  url: `/featureToggle/personHasFeatureAccess/2003`
});

const runAllCompanySearch = (opts: RunAllCompanySearchOptions = {}): AxiosRequestConfig => {
  const {
    countryFilters = [],
    diversityClassificationFilters = [],
    diversityFilters = [],
    invitedEventCountFilters = [],
    invitedPortalCountFilters = [],
    itemFilters = [],
    page = 0,
    pageSize = 100,
    qualificationFilters = [],
    segmentationFilters = [],
    sourceFilters = [],
    stateFilters = [],
    supplierIntelSchemaName,
    useNewSupplierSearch = false
  } = opts;

  return {
    ...baseSupplierSearchAxiosConfig,
    data: {
      countryFilters,
      diversityClassificationFilters,
      diversityFilters,
      enableRegisteredSupplierFilter: isRegisteredSupplierFilterEnabled(sourceFilters),
      eventsInvitedFilters: invitedEventCountFilters,
      portalsInvitedFilters: invitedPortalCountFilters,
      itemFilters,
      qualificationFilters,
      segmentationFilters,
      stateFilters,
      supplierItemsSchema: supplierIntelSchemaName,
      useNewSupplierSearch
    },
    method: 'post',
    params: {
      page,
      pageSize
    },
    url: '/search/allCompanies'
  };
};

const runSearch = (searchTerm: string, opts: RunSearchOptions = {}): AxiosRequestConfig => {
  const {
    countryFilters = [],
    diversityClassificationFilters = [],
    diversityFilters = [],
    excludePeople = false,
    invitedEventCountFilters = [],
    invitedPortalCountFilters = [],
    isCompanySearch = true,
    itemFilters = [],
    qualificationFilters = [],
    segmentationFilters = [],
    sourceFilters = [],
    stateFilters = [],
    supplierIntelSchemaName,
    viewMode = SEARCH_RESULT_VIEW_MODE.HYBRID,
    useNewSupplierSearch = false
  } = opts;

  return {
    ...baseSupplierSearchAxiosConfig,
    data: {
      countryFilters,
      diversityClassificationFilters,
      diversityFilters,
      enableRegisteredSupplierFilter: isRegisteredSupplierFilterEnabled(sourceFilters),
      eventsInvitedFilters: invitedEventCountFilters,
      portalsInvitedFilters: invitedPortalCountFilters,
      itemFilters,
      qualificationFilters,
      segmentationFilters,
      stateFilters,
      supplierItemsSchema: supplierIntelSchemaName,
      useNewSupplierSearch
    },
    method: 'post',
    params: {
      excludePeople,
      isCompanySearch,
      searchTerm,
      viewMode
    },
    url: '/search'
  };
};

//
// --- Export ---

const APIConfig = {
  Permission: {
    getLoggedInPersonCompany,
    hasScreenPermission,
    hasScreenPermissionForEvent,
    hasScreenPermissionForPortal
  },
  Portal: {
    addCompanyToSupplierGroup,
    addPersonToSupplierGroup,
    createSupplierGroup,
    createSupplierGroupCompany,
    createSupplierGroupPerson,
    getIsViewOnlyForPortal,
    getPortal,
    getSupplierGroups,
    removeSupplierCompanyFromPortal,
    removeSupplierCompanyPersonFromPortal,
    removeSupplierGroup,
    updateSupplierGroup,
    updateSupplierGroupMainContact
  },
  Search: {
    runAllCompanySearch,
    runSearch
  },
  SearchFilter: {
    getSearchFilters,
    getSupplierIntelSchemaName
  },
  Sourcing: {
    checkForDuplicateCompanyName,
    checkForDuplicateSupplierEmail,
    createBidTeam,
    createBidTeamMember,
    getBidEvent,
    getBidEventItems,
    getBidEventItemSupplierList,
    getBidEventMailLog,
    getBidTeams,
    getIsViewOnlyForBidEvent,
    getSupplierRequests,
    getUniqueBuyerCompanies,
    grantSupplierAccess,
    inviteSupplier,
    prepareSupplierCompanyPersonNotifyEmail,
    prepareSupplierCompanyPersonReInviteEmail,
    prepareSupplierCompanyPersonReNotifyEmail,
    removeBidTeam,
    removeSupplierCompanyFromBidTeam,
    removeSupplierCompanyPersonFromBidTeam,
    sendBidEventMail,
    sendSupplierBulkEmail,
    updateBidTeam,
    updateBidTeamApprovalStatus,
    updateBidTeamBidIntention,
    createNewCompany
  },
  SpendQuery: {
    getFrequentContactScores,
    getPointOfContactScoreForPeople
  },
  Supplier: {
    getSupplierVendorMappings
  },
  SupplierList: {
    addGlobalSupplierList,
    addGlobalSupplierListAssignments,
    getActiveInvitedSupplierPersonsByIds,
    getSupplierListPermissionsForProduct,
    getSupplierListResults,
    getSupplierListsByCategory,
    removeGlobalSupplierList,
    updateGlobalSupplierList
  },
  SupplierSearch: {
    getCountryList,
    getDiversityTypeMap,
    getMatchesDiscoveryFiltersMapForEipCompanyKeysIds,
    getMatchesDiscoveryFiltersMapForSupplierIds,
    getRelatedCompaniesByAsapSupplierId,
    getRelatedCompaniesByEipCompanyKey,
    getSupplierUsersByAsapSupplierId,
    getSupplierUsersByEipCompanyKey,
    getNewSupplierSearchFeatureToggleFlag
  }
};

export default APIConfig;
